import { createTheme } from "@mui/material/styles";
import variables from "./partials/_variables.module.scss";
import {
  paperClasses,
  tableContainerClasses,
  tableCellClasses,
} from "@mui/material";
declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    containedSmall: true;
    icon: true;
    outlinedSmall: true;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    avatarInitial: true;
  }
}

export const theme = createTheme({
  palette: {
    primary: {
      main: variables.mainBlue,
      dark: variables.darkBlue,
      light: variables.ligthBlue,
    },
  },
  typography: {
    fontFamily: `${variables.fontRubik}`,
    h1: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "2rem",
      fontWeight: "bold",
      letterSpacing: "0.01em",
      lineHeight: "40px",
    },
    h2: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "1.5rem",
      fontWeight: "600",
      letterSpacing: 0,
      lineHeight: "32px",
    },
    h3: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "1rem",
      fontWeight: "500",
      letterSpacing: "0.01em",
      lineHeight: "24px",
    },
    subtitle1: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "1rem",
      fontWeight: "400",
      letterSpacing: "0.002em",
      lineHeight: "24px",
      textTransform: "uppercase",
    },
    subtitle2: {
      color: variables.tableGray,
      fontFamily: variables.fontNunito,
      fontSize: "1.5rem",
      fontWeight: "700",
      letterSpacing: "0.002em",
      lineHeight: "32px",
    },
    button: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "1rem",
      fontWeight: "600",
      letterSpacing: "0.01em",
      lineHeight: "24px",
    },
    body1: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "1rem",
      fontWeight: "400",
      letterSpacing: "0.002em",
      lineHeight: "24px",
    },
    body2: {
      fontFamily: `${variables.fontNunito}`,
      fontSize: "1rem",
      fontWeight: "400",
      lineHeight: "24px",
    },
    caption: {
      fontFamily: `${variables.fontRubik}`,
      fontSize: "0.75rem",
      fontWeight: "400",
      letterSpacing: "0.006em",
      lineHeight: "16px",
    },
  },

  components: {
    /* ==================== TABLE ==================== */
    MuiTableContainer: {
      styleOverrides: {
        root: {
          flexShrink: 0,
          minWidth: 500,
          overflowX: "auto",
          padding: "0 1rem 1.38rem 1rem",
          width: "auto",
          [`&.${paperClasses.root}.${tableContainerClasses.root}`]: {
            border: `1px solid ${variables.disabledGray}`,
            borderRadius: "8px",
            boxShadow: "0px 5px 10px rgba(29, 63, 156, 0.1)",
          },
          [`.${tableCellClasses.root}`]: {
            color: variables.paragraphBaseline,
            fontFamily: "Rubik",
            fontSize: "0.875rem",
            fontStyle: "normal",
            fontWeight: "400",
            letterSpacing: "0.002em",
            lineHeight: "24px",
          },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          [`.${tableCellClasses.root}`]: {
            fontSize: "1rem",
            letterSpacing: "0.002em",
            textTransform: "uppercase",
          },
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          backgroundColor: variables.neutralGray3,
          borderRadius: "8px",
          color: variables.neutralGray1,
          fontSize: "0.875rem",
          fontFamily: variables.fontRubik,
          padding: "4px 16px"
        }
      }
    },

    MuiTableBody: {
      styleOverrides: {
        root: {
          [`.${tableCellClasses.root}`]: {
            borderBottom: 0,
          },
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          "@media (min-width: 0px)": {
            minHeight: "56px",
          },
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
        },
      },
    },

    MuiTypography: {
      variants: [
        {
          props: { variant: "avatarInitial" },
          style: {
            alignItems: "center",
            backgroundColor: variables.darkBlue,
            borderRadius: "50%",
            color: variables.backgroundGray,
            display: "inline-flex",
            fontFamily: "Rubik, sans-serif",
            height: "2rem",
            justifyContent: "center",
            width: "2rem",
          },
        },
      ],
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          transform: "translate(14px, 12px) scale(1)",
          "&&.MuiInputLabel-shrink": {
            transform: "translate(14px, -9px) scale(0.75)",
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
        },

        input: {
          padding: "12px 14px",
        },
      },
    },

    MuiButton: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          fontFamily: "Rubik",
          fontStyle: "normal",
          textTransform: "none",
        },

        contained: {
          alignItems: "center",
          color: variables.backgroundWhite,
          backgroundColor: variables.darkBlue,
          borderRadius: "4px",
          display: "inline-flex",
          fontSize: "1rem",
          fontWeight: 600,
          justifyContent: "center",
          lineHeight: "24px",
          letterSpacing: "0.01rem",
          padding: "0.5rem",
          textTransform: "capitalize",
          "&:hover": {
            backgroundColor: variables.darkBlue,
            opacity: "0.8",
          },
        },

        outlined: {
          alignItems: "center",
          backgroundColor: variables.backgroundWhite,
          border: `1px solid ${variables.mainBlue}`,
          borderRadius: "4px",
          color: variables.mainBlue,
          display: "inline-flex",
          fontSize: "1rem",
          fontWeight: 600,
          gap: "8px",
          justifyContent: "center",
          lineHeight: "24px",
          letterSpacing: "0.01rem",
          padding: "8px 16px",
          textAlign: "justify",
          textTransform: "capitalize",
          "&:hover": {
            backgroundColor: variables.backgroundWhite,
            opacity: "0.8",
          },
        },
      },
      variants: [
        {
          props: { variant: "containedSmall" },
          style: {
            alignItems: "center",
            backgroundColor: variables.mainBlue,
            borderRadius: "8px",
            color: "#fcfdff",
            display: "inline-flex",
            fontFamily: "Noto Sans",
            fontSize: "0.875rem",
            fontWeight: 700,
            gap: "8px",
            justifyContent: "center",
            lineHeight: "24px",
            letterSpacing: "0.0875rem",
            padding: "8px 16px",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: variables.mainBlue,
              opacity: "0.8",
            },
            ":disabled": {
              backgroundColor: variables.disabledGray,
            },
          },
        },
        {
          props: { variant: "outlinedSmall" },
          style: {
            alignItems: "center",
            backgroundColor: variables.backgroundWhite,
            border: "1px solid #104C97",
            borderRadius: "12px",
            color: "#104C97",
            display: "flex",
            fontFamily: "Noto Sans",
            fontSize: "0.875rem",
            fontWeight: 700,
            gap: "8px",
            justifyContent: "center",
            lineHeight: "24px",
            letterSpacing: "0.0875rem",
            padding: "8px 16px",
            textTransform: "capitalize",
            "&:hover": {
              backgroundColor: variables.backgroundWhite,
              opacity: "0.8",
            },
            ":disabled": {
              backgroundColor: variables.disabledGray,
            },
          },
        },
        {
          props: { variant: "icon" },
          style: {
            display: "contents",
            justifyContent: "end",
            padding: 0,
            "&:hover": {
              backgroundColor: "none",
            },
          },
        },
      ],
    },
  },
});
